import { createStore } from 'vuex'
import axios from 'axios'

// Get API base URL from environment variables (for both development and production)
const API_BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:8000'
console.log('Using API endpoint:', API_BASE_URL)

// Set baseURL and timeout for all requests
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds for better reliability
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

// Add response interceptor to handle common errors
api.interceptors.response.use(
  response => response,
  error => {
    console.error('API Error:', error);
    if (error.code === 'ECONNABORTED') {
      console.log('Request timeout');
    }
    return Promise.reject(error);
  }
);

export default createStore({
  state: {
    messages: [],
    isLoading: false,
    error: null,
    voiceOutputEnabled: false,
    audioPlayer: null
  },
  mutations: {
    ADD_MESSAGE(state, message) {
      state.messages.push(message);
      
      // Only keep the last message from each sender
      // This effectively makes it a single conversation turn
      const userMessage = state.messages.filter(m => m.sender === 'user').pop();
      const aiMessage = state.messages.filter(m => m.sender === 'ai').pop();
      
      state.messages = [];
      
      if (userMessage) {
        state.messages.push(userMessage);
      }
      
      if (aiMessage) {
        state.messages.push(aiMessage);
      }
    },
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    TOGGLE_VOICE_OUTPUT(state) {
      state.voiceOutputEnabled = !state.voiceOutputEnabled
      console.log('Voice output enabled:', state.voiceOutputEnabled)
    },
    ENABLE_VOICE_OUTPUT(state) {
      state.voiceOutputEnabled = true
      console.log('Voice output explicitly enabled')
    },
    SET_AUDIO_PLAYER(state, player) {
      // Clean up previous audio player if exists
      if (state.audioPlayer) {
        try {
          state.audioPlayer.pause();
          URL.revokeObjectURL(state.audioPlayer.src); // Free up memory
        } catch (error) {
          console.error('Error cleaning up previous audio player:', error);
        }
      }
      state.audioPlayer = player;
    },
    CLEAR_MESSAGES(state) {
      state.messages = []
    }
  },
  actions: {
    async sendTextMessage({ commit, state }, message) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        // Add user message to chat
        commit('ADD_MESSAGE', {
          content: message,
          sender: 'user',
          timestamp: new Date()
        })
        
        console.log('Sending message to API:', message);
        console.log('Voice output enabled:', state.voiceOutputEnabled);
        
        if (state.voiceOutputEnabled) {
          // Request voice response
          const response = await api.post('/api/chat/text', 
            { 
              message: message,
              voice_response: true 
            },
            { 
              responseType: 'blob' 
            }
          );
          
          console.log('Received voice response');
          
          // Create audio URL and play
          const audioUrl = URL.createObjectURL(response.data);
          
          // Add AI response to chat with audio URL
          commit('ADD_MESSAGE', {
            content: 'Voice response',
            sender: 'ai',
            timestamp: new Date(),
            audioUrl: audioUrl
          });
          
          // Create and play audio
          const player = new Audio(audioUrl);
          
          // Add event handlers for better error tracking
          player.onloadedmetadata = () => {
            console.log("Audio duration:", player.duration);
          };
          
          player.onended = () => {
            console.log("Audio playback completed");
            commit('SET_AUDIO_PLAYER', null);
          };
          
          player.onerror = (e) => {
            console.error("Audio error:", e);
            commit('SET_ERROR', "Error playing audio. Please try again.");
            commit('SET_AUDIO_PLAYER', null);
          };
          
          try {
            // Set the audio player in state
            commit('SET_AUDIO_PLAYER', player);
            
            // Start playback with user interaction handling
            setTimeout(() => {
              const playPromise = player.play();
              if (playPromise !== undefined) {
                playPromise.catch(error => {
                  console.error('Error playing audio:', error);
                  // Don't show error - instead use a more reliable approach
                  player.muted = true;
                  player.play().then(() => {
                    player.muted = false;
                    player.currentTime = 0;
                    player.play();
                  }).catch(e => {
                    console.error('Still cannot play audio:', e);
                    commit('SET_ERROR', "Click anywhere on screen to enable audio");
                  });
                });
              }
            }, 100);
          } catch (error) {
            console.error("Error playing audio:", error);
            commit('SET_ERROR', "Error with audio playback. Please try again.");
          }
        } else {
          // Request text response
          const response = await api.post('/api/chat/text', {
            message: message,
            voice_response: false
          });
          
          console.log('Received text response:', response.data);
          
          // Add AI response to chat
          commit('ADD_MESSAGE', {
            content: response.data.response,
            sender: 'ai',
            timestamp: new Date()
          });
        }
        
        commit('SET_LOADING', false);
        
      } catch (error) {
        console.error('Error sending text message:', error);
        let errorMessage = 'Failed to send message. Please try again.';
        
        if (error.message === 'Network Error') {
          errorMessage = 'Cannot connect to the server. Please ensure the backend is running.';
        } else if (error.response) {
          errorMessage = `Server error: ${error.response.status} - ${error.response.data.detail || 'Unknown error'}`;
        }
        
        commit('SET_ERROR', errorMessage);
        commit('SET_LOADING', false);
        throw error;
      }
    },
    
    async sendVoiceMessage({ commit, state }, audioBlob) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        // Add user message placeholder
        commit('ADD_MESSAGE', {
          content: 'Processing your voice message...',
          sender: 'user',
          timestamp: new Date()
        })
        
        // Validate the audio blob
        if (!audioBlob || (audioBlob instanceof Blob && audioBlob.size === 0)) {
          console.error('Invalid audio blob received:', audioBlob);
          throw new Error('Empty or invalid audio recording. Please try again.');
        }
        
        // Create form data with explicit filename
        const formData = new FormData();
        
        // Use the blob directly without re-wrapping it
        formData.append('audio_file', audioBlob, 'voice.webm');
        formData.append('voice_response', state.voiceOutputEnabled);
        
        console.log('Sending voice message to API:', audioBlob.type, audioBlob.size);
        console.log('Voice response enabled:', state.voiceOutputEnabled);
        
        // Log the audio blob details for debugging
        console.log('Audio blob type:', audioBlob.type);
        console.log('Audio blob size:', audioBlob.size);
        
        if (state.voiceOutputEnabled) {
          // Request voice response with additional debugging
          console.log('Sending API request for voice response');
          try {
            const response = await api.post('/api/chat/voice', 
              formData,
              { 
                headers: {
                  'Content-Type': 'multipart/form-data'
                },
                responseType: 'blob',
                timeout: 30000 // Extended timeout for voice processing
              }
            );
            
            console.log('Received voice response', response);
            
            // Create audio URL
            const audioUrl = URL.createObjectURL(response.data);
            
            // Update user message with transcription from header
            let transcription = 'Voice message';
            if (response.headers && response.headers['x-transcription']) {
              transcription = response.headers['x-transcription'];
              console.log('Transcription from header:', transcription);
            }
            
            // Update user message with transcription
            commit('ADD_MESSAGE', {
              content: transcription,
              sender: 'user',
              timestamp: new Date()
            });
            
            // Add AI response with audio
            commit('ADD_MESSAGE', {
              content: 'Voice response',
              sender: 'ai',
              timestamp: new Date(),
              audioUrl: audioUrl
            });
            
            // Play audio response
            const player = new Audio(audioUrl);
            
            player.onended = () => {
              console.log('Audio playback complete');
              commit('SET_AUDIO_PLAYER', null);
            };
            
            player.onerror = (error) => {
              console.error('Audio playback error:', error);
              commit('SET_ERROR', 'Error playing the response audio.');
              commit('SET_AUDIO_PLAYER', null);
            };
            
            // Store the player in state for management
            commit('SET_AUDIO_PLAYER', player);
            
            // Play the audio with error handling
            const playPromise = player.play();
            if (playPromise !== undefined) {
              playPromise.catch(error => {
                console.error('Error playing audio:', error);
                commit('SET_ERROR', 'Click on the screen to enable audio playback');
              });
            }
            
          } catch (error) {
            console.error('Error with voice response:', error);
            commit('SET_ERROR', 'Failed to get a voice response. Please try again.');
            
            // Try to get at least a text response
            commit('ADD_MESSAGE', {
              content: 'Could not process audio response. Please try again.',
              sender: 'ai',
              timestamp: new Date()
            });
          }
        } else {
          // Request text-only response
          const response = await api.post('/api/chat/voice', 
            formData,
            { 
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          
          console.log('Received text response:', response.data);
          
          // Update user message with transcription
          if (response.data.transcription) {
            commit('ADD_MESSAGE', {
              content: response.data.transcription,
              sender: 'user',
              timestamp: new Date()
            });
          }
          
          // Add AI response to chat
          commit('ADD_MESSAGE', {
            content: response.data.response,
            sender: 'ai',
            timestamp: new Date()
          });
        }
        
        commit('SET_LOADING', false);
        return { success: true };
        
      } catch (error) {
        console.error('Error sending voice message:', error);
        let errorMessage = 'Failed to process voice message. Please try again.';
        
        if (error.message === 'Network Error') {
          errorMessage = 'Cannot connect to the server. Please ensure the backend is running.';
        } else if (error.response) {
          errorMessage = `Server error: ${error.response.status} - ${error.response.statusText}`;
        }
        
        commit('SET_ERROR', errorMessage);
        commit('SET_LOADING', false);
        throw error;
      }
    },
    
    toggleVoiceOutput({ commit }) {
      commit('TOGGLE_VOICE_OUTPUT')
    },
    
    enableVoiceOutput({ commit }) {
      commit('ENABLE_VOICE_OUTPUT')
    }
  },
  getters: {
    getMessages: state => state.messages,
    isLoading: state => state.isLoading,
    getError: state => state.error,
    isVoiceOutputEnabled: state => state.voiceOutputEnabled
  }
}) 